import { onlineManager } from "@tanstack/react-query";
import { reportError } from "@visioni/a3s-error-reporting";
import { requestErrorStatus, requestErrorType } from "./error-type";
import { ApiError } from "./request";

/**
 * Statuses the app expects and handles: an expired session (401), access denied
 * (403), something deleted or an old link (404, except an unknown route), a
 * conflict (409) and rate limiting (429). Other 4xx, such as 400 and 422, usually
 * mean the app sent a request the API rejects, so they are reported.
 */
const EXPECTED_STATUSES: ReadonlySet<number> = new Set([401, 403, 404, 409, 429]);

/**
 * The 404 message the API Lambdas' router (Powertools `event-handler`,
 * `http/Router.js`) sends when no route matches, e.g. `Route /api/v1/x for method
 * GET not found`. Such a 404 means the app called an endpoint that does not exist,
 * not that a resource is gone. Re-check the wording when upgrading Powertools.
 */
const UNKNOWN_ROUTE = /Route \S+ for method [A-Z]+ not found/;

/** `ApiError` keeps the response body apart from its generic message; other errors carry it in the message. */
function responseText(error: unknown): string {
  if (error instanceof ApiError) return error.body ?? "";
  return error instanceof Error ? error.message : "";
}

/**
 * What `fetch` throws when the request never reached the server, per platform:
 * React Native, Chromium, Safari, Firefox. Only a `TypeError` with one of these
 * messages counts; a `TypeError` from a bug carries a different message.
 */
const NETWORK_FAILURE_MESSAGES = [
  "Network request failed",
  "Failed to fetch",
  "Load failed",
  "NetworkError when attempting to fetch resource",
];

function isNetworkFailure(error: unknown): boolean {
  return (
    error instanceof TypeError &&
    (!onlineManager.isOnline() || NETWORK_FAILURE_MESSAGES.some((message) => error.message.includes(message)))
  );
}

/**
 * A fixed name for a query or mutation key: its first part when that is a name
 * (`["cameras", tenantId, …]` → `cameras`), or, for a client-api endpoint, the
 * feature and resource it names (`[{ feature: "media", resource: "snapshots", … }]`
 * → `media/snapshots`). Never later parts or params: they can hold user-typed
 * text, such as a search.
 */
function operationName(key: readonly unknown[] | undefined): string {
  const first = key?.[0];
  if (typeof first === "string") {
    return first;
  }
  if (
    typeof first === "object" &&
    first !== null &&
    "feature" in first &&
    "resource" in first &&
    typeof first.feature === "string" &&
    typeof first.resource === "string"
  ) {
    return `${first.feature}/${first.resource}`;
  }
  return "unnamed";
}

/**
 * Reports a failed query or mutation unless its status is expected
 * (`EXPECTED_STATUSES`), it timed out, or it never reached the server. Each
 * operation name and status is its own issue in Sentry, because every request
 * fails from the same place in the API client.
 */
export function reportRequestError(
  error: unknown,
  operation: { readonly kind: "query" | "mutation"; readonly key: readonly unknown[] | undefined },
): void {
  const type = requestErrorType(error);
  const status = requestErrorStatus(error);
  const unknownRoute = status === 404 && UNKNOWN_ROUTE.test(responseText(error));
  if (
    type === "timeout" ||
    isNetworkFailure(error) ||
    (status !== undefined && EXPECTED_STATUSES.has(status) && !unknownRoute)
  ) {
    return;
  }
  const name = operationName(operation.key);
  const statusLabel = status === undefined ? "none" : String(status);
  reportError(error, {
    tags: { [operation.kind]: name, request_error: unknownRoute ? "route_not_found" : type, http_status: statusLabel },
    groupBy: [operation.kind, name, statusLabel],
  });
}
